import { ChangeDetectorRef, Directive, ElementRef, Input, NgZone, OnDestroy, OnInit } from '@angular/core';
import { Status } from '@df/core/status';
import { NgInject } from '@df/ng/ng-inject';
import { Renderer } from '@df/ng/renderer';
import { Subscription } from 'rxjs';

/**
 *
 * Action component is kind button but supporting statuses like busy, success, error, disabled
 * Unlike regular button it has template.
 *
 * @deprecated please see new buttons synatex on template store
 *
 * @usage
 * ```html
 * <action [status]='status' ></action
 * ```
 */
@Directive()
export class ActionComponentDef implements OnInit, OnDestroy {
    /**
     * If results is visible
     * @type {boolean}
     */
    public isResultVisible = false;

    /**
     * Result class
     * @type {string}
     */
    public resultClass?: string;

    /**
     * Status subscription
     * @type {[type]}
     */
    protected subscription?: Subscription;

    /**
     * Status object set throut status setter
     * @type {[type]}
     */
    protected _status?: Status;

    /**
     * Timeout id for dropping showResult flag
     */
    protected showTimeout?: number;

    /**
     * Default duration time
     * @type {number}
     */
    protected _duration = 10;

    /**
     * Success message setter
     */
    @Input()
    success?: string;

    /**
     * Status setter
     * Status is required
     */
    @Input()
    set status(status: Status | undefined) {
        this._status = status;

        if (this.subscription) {
            this.subscription.unsubscribe();
            this.subscription = undefined;
        }

        if (status) {
            this.subscription = status.subscribe(() => this.update());
            this.update();
        } else if (status === undefined) {
            this.reset();
        }
    }

    get status() {
        return this._status;
    }

    @NgInject(NgZone)
    private ngZone!: NgZone;

    /**
     * Duration setter
     * Pass 0 or 'permanent' not to reset state automatically
     */
    @Input()
    set duration(duration: number | string) {
        if (typeof duration === 'string') {
            duration = duration === 'permanent' ? 0 : parseInt(duration, 10);
        }
        this._duration = duration;
    }

    /**
     * Class constructor
     * @param {ElementRef} protected ngEl
     * @param {Renderer}   protected renderer
     */
    public constructor(
        protected ngEl: ElementRef,
        protected renderer: Renderer,
        protected _ref: ChangeDetectorRef
    ) {}

    /**
     * Sync on init
     */
    ngOnInit() {
        this.update();
    }

    /**
     * Remove subscription on componetn destruction
     */
    ngOnDestroy() {
        if (this.subscription) {
            this.subscription.unsubscribe();
        }
    }

    /**
     * Syncs action state with status state
     * called on status next
     */
    protected update() {
        // cancel previous
        this.reset();

        if (!this._status) {
            return;
        }

        // if busy then just hide message and set busy status class
        if (this._status.busy) {
            return this.set(this._status.superBusy, 'busy');
        }

        // if not busy we can show message
        if (!this._status.result) {
            return this.set(false, undefined);
        }

        this.set(true, this._status.error ? 'error' : 'success');

        // set timeout to remove show flag
        if (this._duration) {
            this.ngZone.runOutsideAngular(() => {
                this.showTimeout = <number>(<any>setTimeout(() => this.reset(true), this._duration * 1000));
            });
        }
    }

    /**
     * Resets component to initial stae
     */
    protected reset(status = false): void {
        this.set(false, undefined);
        if (this.showTimeout) {
            clearTimeout(this.showTimeout);
        }
        this.showTimeout = undefined;
        this.isResultVisible = false;
        this.resultClass = undefined;

        if (status && this._status) {
            this._status.reset();
        }

        this.detectChanges();
    }

    /**
     * Sets state of component
     * @param {boolean} showResult  [description]
     * @param {string}  statusClass [description]
     */
    protected set(showResult: boolean, statusClass: string | undefined) {
        this.isResultVisible = showResult;
        this.resultClass = statusClass;

        ['busy', 'error', 'success'].forEach(className => {
            this.renderer.setClass(this.ngEl.nativeElement, className, className === statusClass);
        });
        this.onUpdate();
        this.detectChanges();
    }

    /**
     * On update hook for inherited classes
     */
    protected onUpdate() {}

    detectChanges() {
        this._ref.detectChanges();
    }
}
